import { settings } from "app";
import { administrationRequests } from "api/administrationApi";
import { Box, Chip, Grid, Tooltip, useMediaQuery } from "@mui/material";
import React from "react";
import {
  ArrayField,
  AutocompleteInput,
  Button,
  Datagrid,
  DateField,
  FunctionField,
  List,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleList,
  SingleFieldList,
  TextField,
} from "react-admin";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import { useNavigate } from "react-router";
import { CLASSES, ClassTypes, MOBILE_BREAKPOINT, StudentServicePath } from "utils/constants";
import RoomField from "server/components/Fields/RoomField";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { ClassesExporter } from "./XlsxExporter";
import { tenants } from "utils/tenants";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import ProfessorField from "server/components/Fields/ProfessorField";
import { MobileListRow } from "features/MobileListRow";
import { formatDateOnlyBg } from "server/formatting/formatDateBg";
import ChangeProfessorDialog from "./ChangeProfessorDialog";

const ListClasses = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const today = new Date().toISOString().split("T")[0];

  const renderSpecialities = record => (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        maxWidth: { xs: "200px", xl: "400px" },
        overflowY: "auto",
      }}
    >
      {record.specialitiesNames?.map((spec, index) => (
        <Tooltip
          title={spec}
          key={index}
          arrow
        >
          <Chip
            key={index}
            label={spec}
            size="small"
            variant="outlined"
          />
        </Tooltip>
      ))}
    </Box>
  );

  // Just the buttons (no container), so the desktop "Действия" cell can stack them with ChangeProfessorDialog.
  const actionButtons = (record, labelManagePresence = "Присъствие", labelManageEquipment = "Оборудване") => {
    const isToday = record.date === today;
    const hasAttendancePermission = checkPermission(getPermissions(), permissions.canPreviewClassAttendance);
    const isDateAllowed = settings.tenant === tenants.MU ? isToday : true;
    return (
      <>
        {hasAttendancePermission && isDateAllowed && (
          <Button
            label={labelManagePresence}
            color="primary"
            startIcon={<CoPresentIcon />}
            onClick={() =>
              navigate(`/${StudentServicePath}${CLASSES}/attendance/${record.id}/edit`, {
                state: {
                  specialitiesNames: record?.specialitiesNames,
                  studyGroups: record?.studyGroups,
                  disciplineName: record?.disciplineName,
                  classTypeId: record?.classTypeId,
                  date: record?.date,
                  startTime: record?.startTime,
                  endTime: record?.endTime,
                },
              })
            }
          />
        )}
        {settings.tenant === tenants.MU && checkPermission(getPermissions(), permissions.canUpdateUsedEquipment) && (
          <Button
            label={labelManageEquipment}
            color="primary"
            startIcon={<ChecklistRtlIcon />}
            onClick={() => navigate(`/${StudentServicePath}${CLASSES}/equipment/${record.id}/edit`)}
          />
        )}
      </>
    );
  };

  // Mobile row actions - centered like before, wrapping when they don't fit.
  const renderActionButtons = record => (
    <Box sx={{ ...settings.style.wrappingActionButtons, justifyContent: "center" }}>{actionButtons(record)}</Box>
  );
  const classesFilter = [
    <ReferenceInput
      source="disciplineId"
      reference={administrationRequests.getDisciplinesSelect()}
      label="Дисциплина"
      filter={{ filterByUser: true }}
      alwaysOn
    >
      <AutocompleteInput
        optionValue="id"
        optionText="name"
        label="Дисциплина"
        filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
        sx={{ width: "300px" }}
      />
    </ReferenceInput>,
    <ReferenceInput
      source="classTypeId"
      reference={administrationRequests.getRoomClassTypesSelect()}
      filter={{ excludeIds: [ClassTypes.Csc] }}
      alwaysOn
    >
      <SelectInput
        optionText="name"
        label="Тип занятия"
        emptyText={false}
      />
    </ReferenceInput>,
    <ProfessorInput
      source="professorEmployeeId"
      alwaysOn
      style={{ width: 300 }}
    />,
    <NumberInput
      label="Учебна група"
      source="studyGroup"
      min={1}
      alwaysOn
      fullWidth
    />,
  ];
  return (
    <List
      actions={<ExportXlsButton exporter={ClassesExporter} />}
      title="Занятия"
      filters={classesFilter}
      sx={settings.style.expandStyle}
    >
      {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Дисциплина", value: record?.disciplineName }]} />
              <MobileListRow fields={[{ label: "Дата", value: formatDateOnlyBg(record?.date) }]} />
            </div>
          )}
          secondaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Специалности", value: renderSpecialities(record) }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Час",
                    value: `${record.startTime.slice(0, 5)} - ${record.endTime.slice(0, 5)}`,
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Учебни групи", value: record?.studyGroups?.join(", ") }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Тип занятие",
                    value: (
                      <ReferenceField
                        source="classTypeId"
                        reference={administrationRequests.getRoomClassTypesSelect()}
                        sortable={false}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Зала", value: <RoomField label="Зала" /> }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Преподавател",
                    value: (
                      <ProfessorField
                        source="professorId"
                        label="Преподавател"
                      />
                    ),
                  },
                ]}
              />
              {checkPermission(getPermissions(), permissions.canUpdateClassProfessor) && (
                <Grid sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
                  <ChangeProfessorDialog record={record} />
                </Grid>
              )}
              <MobileListRow fields={[{ label: "Модул", value: record?.curriculumModuleName ?? "-" }]} />
              {renderActionButtons(record)}
            </div>
          )}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          sx={{ ...settings.style.firstExpandIconDatagrid, ...settings.style.compactDatagridCells }}
        >
          <FunctionField
            label="Специалности"
            render={renderSpecialities}
          />

          <TextField
            label="Дисциплина"
            source="disciplineName"
            sortable={false}
          />
          <TextField
            label="Курс"
            source="course"
            sortable={false}
          />

          <DateField
            label="Дата"
            source="date"
            sortable={false}
            locales="bg-BG"
          />
          <FunctionField
            label="Час"
            render={record => `${record.startTime.slice(0, 5)} - ${record.endTime.slice(0, 5)}`}
          />
          <ArrayField
            source="studyGroups"
            label="Учебни групи"
          >
            <SingleFieldList linkType={false}>
              <FunctionField
                render={rec => (
                  <Chip
                    key={rec}
                    label={rec}
                  />
                )}
              />
            </SingleFieldList>
          </ArrayField>
          <ReferenceField
            source="classTypeId"
            reference={administrationRequests.getRoomClassTypesSelect()}
            label="Тип занятие"
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>

          <RoomField label="Зала" />
          <ProfessorField
            source="professorId"
            label="Преподавател"
          />
          <TextField
            label="Модул"
            source="curriculumModuleName"
            emptyText="-"
          />
          {/* All row actions share one column (incl. ChangeProfessorDialog); below xl they stack vertically. */}
          <FunctionField
            label="Действия"
            render={record => (
              <Box sx={settings.style.stackingActionButtons}>
                {checkPermission(getPermissions(), permissions.canUpdateClassProfessor) && (
                  <ChangeProfessorDialog record={record} />
                )}
                {actionButtons(record)}
              </Box>
            )}
          />
        </Datagrid>
      )}
    </List>
  );
};

export default ListClasses;
